import * as logger from "./utils/logger";
import { listInstalledAddins } from "./utils/addinHelper";
import AppManager, { TContextSource } from "./app.manager";
import { DatabaseCache } from "./services/databaseCache";
import Network from "./services/network";
import Api from "./services/api";
import Sync from "./sync/sync";
import { SyncType } from "./sync/sync.types";
import { registerNativeAssetChangeListeners } from "./utils/native/nativeAssetSelection";
import { getCurrentVehicle, setCurrentVehicle } from "./utils/device";
import { dispatcher, DispatcherEvents } from "./utils/dispatcher";
import HosProcess from "./services/hosProcess";
import IoxDataHandler from "./services/ioxDataHandler";
import VehicleProximity from "./services/vehicleProximity";
import VehicleService from "./services/vehicle";
import TranslationService from "./services/translation";
import NotificationCenter from "./services/notificationCenter";
import ServerEvents from "./services/serverEvents";
import WebSocketChannel from "./services/webSocketChannel";
import { AddinService } from "./services/addin";
import Movement from "./services/sharedServices/movement";
import ScreenHandler from "./services/screenHandler";
import { DTCMonitor } from "./services/DTCMonitor";
import { DriveStore } from "./store/reducers";
import Alerts from "./services/alerts";
import News from "./services/news";
import { SecurityService } from "./utils/security";
import { LocalizerService } from "./utils/localizer";
import { ErrorHandler } from "./utils/error.impl";
import { UserService } from "./utils/users";
import { DutyStatusLogService } from "./model/DutyStatusLogs";
import { UserHosRuleSetService } from "./model/UserHosRuleSets";
import PromiseDebounce from "./utils/promiseDebounce";
import { DVIRLogsService } from "./model/DVIRLogsService";
import DrivingStatusService from "./services/drivingStatusService/drivingStatusService";
import { IFeatureFlagsService } from "./services/featureFlags";
import { isIamLogin } from "./utils/uri";
import { Session } from "./services/session";
import { IamService } from "./services/iamService";
import OfflineStatusReminder from "./services/offlineStatusReminder";
import mainScheduler from "./utils/scheduler";
import { IService } from "./modules/serviceBase";
import ScheduledDriverSafetyService from "./modules/driverSafety/service/scheduledDriverSafetyService";
import ScheduledCoachingService from "./modules/coaching/services/scheduledCoachingService";
import { checkRedirectRequired } from "./utils/redirectioner";
import ChangeStartOfDayService from "./services/changeStartOfDayService";
import { Unsubscribe } from "./lib/redux";
import CreateLogWithOmittedDataWarningService from "./services/createLogWithOmittedDataWarningService";
import WebSocketPingService from "./services/webSocketPingService";

interface IAppServicesOptions {
    manager: AppManager;
}

export default class Context {

    api: Api;
    sync: Sync;
    databaseCache: DatabaseCache;
    network: Network;
    manager: AppManager;
    drivingStatusService: DrivingStatusService;
    movement: Movement;
    ioxDataHandler: IoxDataHandler;
    hosProcess: HosProcess;
    vehicleService: VehicleService;
    vehicleProximity: VehicleProximity;
    translationService: TranslationService;
    webSocketChannel: WebSocketChannel;
    notificationCenter: NotificationCenter;
    serverEvents: ServerEvents;
    addinService: AddinService;
    screenHandler: ScreenHandler;
    dtcMonitor: DTCMonitor;
    store: DriveStore;
    alertsService: Alerts;
    newsService: News;
    securityService: SecurityService;
    localizerService: LocalizerService;
    errorHandler: ErrorHandler;
    userService: UserService;
    dutyStatusLogService: DutyStatusLogService;
    dvirLogsService: DVIRLogsService;
    userHosRuleSetService: UserHosRuleSetService;
    sessionService: Session;
    textMessageTimeout: number | undefined;
    tripsDebounce: PromiseDebounce | undefined;
    dutyStatusLogDebounce: PromiseDebounce | undefined;
    iamService: IamService;
    offlineStatusReminder: OfflineStatusReminder;
    changeStartOfDayService: ChangeStartOfDayService;
    createLogWithOmittedDataWarningService: CreateLogWithOmittedDataWarningService;
    pingService: WebSocketPingService;

    // #######################################
    // ||                                   ||
    // || Services for Geotab Drive Modules ||
    // ||                                   ||
    // #######################################
    services: IService[];
    scheduledDriverSafetyService: ScheduledDriverSafetyService;
    scheduledCoachingService: ScheduledCoachingService;

    /**
     * True between startSubscriptions() and stopSubscriptions(). Guards against
     * double-subscribing this Context's services to the shared redux store; see
     * startSubscriptions(). Not a public lifecycle signal — callers should not branch on it.
     */
    private subscriptionsStarted = false;

    /** Tears down the DriveNative asset-change listeners. Null when not native. */
    private unsubscribeNativeAssetChanges: (() => void) | null = null;

    // Serializes native vehicle-selection effects (MYG-149253) so two selections
    // arriving close together can't both read the same stale "previous vehicle".
    private nativeVehicleSelectionChain: Promise<void> = Promise.resolve();
    private lastNativeEffectVehicle: IIdEntity | undefined;

    private readonly storeUnsubscribe: Unsubscribe[] = [];

    constructor(
        options: IAppServicesOptions,
        readonly featureFlagsService: IFeatureFlagsService,
        userName?: string,
        contextSource?: TContextSource
    ) {
        logger.log(logger.Source.SERVICES, "Initializing context", this.user);

        this.manager = options.manager;
        this.movement = this.manager.movement;
        this.store = this.manager.store;

        this.userService = this.manager.userService;
        this.iamService = new IamService(this.store);
        this.api = new Api(this.userService, this.store, userName, this.iamService, this.featureFlagsService, contextSource);

        this.network = new Network(this.api, this.store);
        this.translationService = new TranslationService(this.store, this.api, this.user, this.userService, this.featureFlagsService);
        this.errorHandler = new ErrorHandler(this.store, this.userService, this.translationService, this.featureFlagsService, window.errorTracker);
        this.alertsService = new Alerts(this.store, this.translationService);
        this.localizerService = new LocalizerService(this.store, this.userService);
        this.securityService = new SecurityService(this.store, this.userService, this.featureFlagsService);
        this.newsService = new News(this.store, this.api, this.translationService, this.securityService);
        this.dutyStatusLogService = new DutyStatusLogService(this.store, this.userService, this.featureFlagsService);
        this.dvirLogsService = new DVIRLogsService(this.store, this.userService);
        this.userHosRuleSetService = new UserHosRuleSetService(this.store, this.api);

        this.hosProcess = new HosProcess(
            this.api,
            this.translationService,
            this.securityService,
            this.userService,
            this.dutyStatusLogService,
            this.store,
            this.userHosRuleSetService,
            this.featureFlagsService
        );
        this.sync = new Sync(
            this.api,
            this.translationService,
            this.securityService,
            this.userService,
            this.dutyStatusLogService,
            this.dvirLogsService,
            this.store,
            this.hosProcess,
            this.featureFlagsService,
            (user: string) => this.manager.getCachedContext(user)
        );

        // DriveNative can change trailers, shipments and the vehicle on its own
        // native screen (MYG-133100); re-sync so this app's cache doesn't go
        // stale. Registered here (not in a React tree) because SyncContext is
        // per-page and the driver can be anywhere when the change lands.
        this.lastNativeEffectVehicle = getCurrentVehicle() || undefined;
        this.unsubscribeNativeAssetChanges = registerNativeAssetChangeListeners((changed, deviceId) => {
            // Adopt the pushed device id directly rather than routing through
            // VehicleService.selectVehicle — the native shell already created
            // the server-side DriverChange, so re-creating it here would
            // double-write. setCurrentVehicle is a plain store setter with no
            // DriverChange side effect.
            if (changed === "vehicle") {
                const oldVehicle = getCurrentVehicle() || undefined;
                // Native sends the NoDeviceId sentinel (not null) for "no vehicle".
                const newVehicle = { id: deviceId || "NoDeviceId" };
                // Guards against this listener firing more than once for the
                // same change (team driving, or an uncached Context that leaks
                // a listener on logout) — minimum fix, not the leak itself.
                if (oldVehicle?.id !== newVehicle.id) {
                    setCurrentVehicle(newVehicle);
                    // AppManager.onVehicleSelected runs the legacy Login/Logoff and
                    // sync.onSelectNewVehicle effects, but does not create a DriverChange —
                    // selectVehicle creates that before calling it. The native outbox already
                    // owns that write, so this is safe.
                    this.nativeVehicleSelectionChain = this.nativeVehicleSelectionChain
                        .then(async () => {
                            const effectOldVehicle = this.lastNativeEffectVehicle;
                            this.lastNativeEffectVehicle = newVehicle;
                            await this.manager.onVehicleSelected(this, newVehicle, effectOldVehicle, true);
                            dispatcher.trigger(DispatcherEvents.VehicleSelected, newVehicle, effectOldVehicle);
                        })
                        .catch((error: Error) => logger.error(
                            logger.Source.NATIVE,
                            `native vehicle selection effects failed: ${ error.message }`
                        ));
                }
            }

            // Deliberately excludes DriverChange: re-validating the assignment
            // in the same tick races the native outbox drain and would read
            // the vehicle's *previous* assignment, firing a false "no longer
            // assigned" dialog. It reconciles on the next natural sync instead.
            const syncTypes = changed === "trailers"
                ? [SyncType.TrailerAttachments]
                : changed === "shipments"
                    ? [SyncType.Shipments]
                    : [SyncType.TrailerAttachments, SyncType.Shipments];
            this.sync.synchronize(false, null, syncTypes, "user")
                .catch((error: Error) => logger.error(
                    logger.Source.NATIVE,
                    `nativeAssetChange sync failed: ${ error.message }`
                ));
        });

        this.drivingStatusService = new DrivingStatusService(this.store, this.userHosRuleSetService, this.movement, this.featureFlagsService, this);

        this.vehicleService = new VehicleService(
            this.api,
            this.user,
            this.sync,
            this.hosProcess,
            this.alertsService,
            this.userService,
            this.store,
            this.dutyStatusLogService,
            this.translationService
        );
        this.webSocketChannel = new WebSocketChannel(this.api, this.user);
        // Reconnect must re-establish this Context's channel topic subscriptions, not merely
        // the socket: initializeChannel() builds a new CometChannel with a new channelId and
        // re-subscribes only drive-pong, so vehiclemove / DutyStatusLog / DVIRLog / TextMessage
        // would otherwise stay bound to the discarded channel — a healthy socket delivering
        // nothing. reconnectSubscriptions() is the narrow form for that; passing the full
        // startSubscriptions() here also restarted store-observing services and resurrected
        // stopped contexts. See MYG-143588.
        this.pingService = new WebSocketPingService(this.webSocketChannel, this.featureFlagsService, this.user, () => this.reconnectSubscriptions());

        this.notificationCenter = new NotificationCenter(this.webSocketChannel);
        this.serverEvents = new ServerEvents(
            this.store,
            this.api,
            this.notificationCenter,
            this.user,
            this.errorHandler,
            this.userService,
            this.securityService,
            this.featureFlagsService,
            this.translationService,
            null,
            null
        );
        this.addinService = new AddinService(
            this.api,
            this.hosProcess,
            this.sync,
            this.manager.router,
            this.store,
            this.userService,
            this.translationService,
            this.dutyStatusLogService
        );
        this.ioxDataHandler = new IoxDataHandler(this.api, this.sync, this.userService, this.dutyStatusLogService, this.store, this.hosProcess);
        this.vehicleProximity = new VehicleProximity(this.api, this.store, this.dutyStatusLogService);
        this.screenHandler = new ScreenHandler(this.api, this.userService, this.vehicleProximity, this.store, this.hosProcess, this.featureFlagsService);
        this.dtcMonitor = new DTCMonitor(this.store, this.api, this.sync, this.dutyStatusLogService, this.userHosRuleSetService, this.securityService);
        this.databaseCache = new DatabaseCache(this.api, this.movement, this.sync, this.store, this.userService, this.dutyStatusLogService, this.featureFlagsService, this.errorHandler);
        this.addinService.setInstalledAddins(() => listInstalledAddins(this.databaseCache, this.userService.getCurrentUser()));
        this.sessionService = new Session(this.store, this.api, this.userService, this.dutyStatusLogService, this.featureFlagsService);
        this.offlineStatusReminder = new OfflineStatusReminder(this.translationService.getTranslator(), this.store, this.userService, this.securityService);
        this.changeStartOfDayService = new ChangeStartOfDayService(
            this.userService,
            this.sync,
            this.hosProcess,
            this.dutyStatusLogService,
            this.securityService,
            this.translationService,
            this.store,
            this.api.getUser()
        );
        this.createLogWithOmittedDataWarningService = new CreateLogWithOmittedDataWarningService(
            this.userService,
            this.translationService,
            this.store,
            this.api.getUser()
        );
        // ################################################################
        // ||                                                            ||
        // || Registration of Service instances for Geotab Drive Modules ||
        // ||                                                            ||
        // ################################################################
        this.services = [];
        this.scheduledDriverSafetyService = this.registerService(
            new ScheduledDriverSafetyService(
                this.store,
                this.api,
                mainScheduler,
                this.userService,
                this.securityService
            )
        );
        this.scheduledCoachingService = this.registerService(
            new ScheduledCoachingService(
                this.store,
                this.api,
                mainScheduler,
                this.userService,
                this.securityService,
                this.featureFlagsService
            )
        );
        // this.someService = this.registerService<SomeService>(new SomeService(...));
    }
    // someService: SomeService;

    get user(): string | null {
        let user = this.api ? this.api.getUser() : null;
        if (user) {
            return user.name;
        }
        return null;
    }

    /**
     * Starts subscriptions for SubscriptionsServices (Services with events/listeners)
     * These listeners/events are only active while the current Context (a user's collection of services) is in the
     * foreground
     * If background services are needed, see AppManager class and "applyActionToAllServices"
     *
     * If the user is in the process of logging in with Auth0, we don't want to start subscriptions
     * Once they finish logging in, this method will be called again and the subscriptions will start
     */
    startSubscriptions() {
        // if we are going to redirect, we don't need to start subscription
        if (isIamLogin() && checkRedirectRequired(this)) {
            return;
        }

        // Subscribing twice without an intervening stopSubscriptions() leaves duplicate
        // listeners on the shared redux store. Two ScreenHandlers, for example, turn one
        // driving-state change into two lockScreen() calls (MYG-143588). Report and bail
        // rather than compounding the damage — the stack identifies the caller.
        if (this.subscriptionsStarted) {
            logger.error(
                logger.Source.SERVICES,
                `Context.startSubscriptions called while already subscribed — ignoring duplicate. ${ new Error().stack }`,
                this.user
            );
            return;
        }
        this.subscriptionsStarted = true;

        this.translationService.startSubscriptions();
        this.webSocketChannel.startSubscriptions();
        this.serverEvents.startSubscriptions();
        this.ioxDataHandler.startSubscriptions();
        this.screenHandler.startSubscriptions();
        this.vehicleService.startSubscriptions();
        this.hosProcess.startSubscriptions();
        this.alertsService.startSubscriptions();
        this.drivingStatusService.startSubscriptions();
        this.offlineStatusReminder.startSubscriptions();

        // ##################################################
        // ||                                              ||
        // || Start subscriptions for Geotab Drive Modules ||
        // ||                                              ||
        // ##################################################
        this.services.forEach((service: IService) => {
            service.startSubscriptions();
        });
    }

    /**
     * Stops subscriptions for SubscriptionsServices (Services with events/listeners)
     * When switching away, all subscriptions for a user's services are stopped
     */
    stopSubscriptions() {
        // Unsubscribe from the listener for changes to the driving status service feature flag
        this.storeUnsubscribe && this.storeUnsubscribe.forEach(unsub => unsub());

        this.vehicleService.stopSubscriptions();
        this.webSocketChannel.stopSubscriptions();
        this.serverEvents.stopSubscriptions();
        this.ioxDataHandler.stopSubscriptions();
        this.screenHandler.stopSubscriptions();
        this.translationService.stopSubscriptions();
        this.hosProcess.stopSubscriptions();
        this.alertsService.stopSubscriptions();
        this.drivingStatusService.stopSubscriptions();
        this.offlineStatusReminder.stopSubscriptions();

        // #################################################
        // ||                                             ||
        // || Stop subscriptions for Geotab Drive Modules ||
        // ||                                             ||
        // #################################################
        this.services.forEach((service: IService) => {
            service.stopSubscriptions();
        });

        this.subscriptionsStarted = false;
    }

    /**
     * Starts tasks for ScheduledServices that have been registered with the Context.
     */
    startTasks() {
        this.services.forEach((service: IService) => {
            service.startTask();
        });
    }

    /**
     * Stops tasks for ScheduledServices that have been registered with the Context.
     */
    stopTasks() {
        this.services.forEach((service: IService) => {
            service.stopTask();
        });
    }

    onServerEvent(eventName: string, handler: any): void {
        this.serverEvents.on(eventName, handler);
    }

    offServerEvent(eventName: string, handler: any): void {
        this.serverEvents.off(eventName, handler);
    }

    registerService<T extends IService>(service: T) {
        this.services.push(service);
        return service;
    }

    destroy(isIamProviderSignout: boolean) {
        this.unsubscribeNativeAssetChanges?.();
        this.unsubscribeNativeAssetChanges = null;
        this.screenHandler.destroy();
        this.hosProcess.destroy();
        this.sync.destroy();
        this.vehicleService.destroy();
        this.ioxDataHandler.destroy();
        this.databaseCache.destroy();
        this.drivingStatusService.destroy();
        this.network.destroy();
        this.translationService.destroy();
        this.notificationCenter.destroy();
        this.serverEvents.destroy();
        this.dtcMonitor.destroy();
        this.alertsService.destroy();
        this.newsService.destroy();
        this.offlineStatusReminder.destroy();
        this.pingService.destroy();
        if (this.api) {
            this.api.destroy();
        }
        this.iamService.destroy(isIamProviderSignout);

        // ########################################################
        // ||                                                    ||
        // || Destroy service instances for Geotab Drive Modules ||
        // ||                                                    ||
        // ########################################################
        this.services.forEach((service: IService) => {
            service.destroy();
        });
    }

    /**
     * Re-establishes this Context's WebSocket topic subscriptions after the channel has been
     * torn down and rebuilt (see `WebSocketPingService`).
     *
     * `WebSocketChannel.startSubscriptions()` replaces the underlying CometChannel, and the
     * new channel only carries the `drive-pong` topic. Every business topic
     * (`vehiclemove`, `DutyStatusLog`, `DVIRLog`, `TextMessage`, `drive-command`, ...) was
     * registered against the discarded channel object and must be re-registered, otherwise
     * the socket looks healthy while no notifications arrive — the silent-delivery failure
     * this ping/pong work exists to prevent.
     *
     * This is deliberately narrower than `startSubscriptions()`: only the services that own
     * channel topics are restarted. Services that subscribe to the redux store
     * (`screenHandler`, `drivingStatusService`, `alertsService`, ...) are untouched, because
     * re-running them adds a second store subscriber for the same Context — with a co-driver
     * that meant two ScreenHandlers turning one driving-state change into two lockScreen()
     * calls. See MYG-143588.
     */
    private reconnectSubscriptions() {
        if (!this.subscriptionsStarted) {
            // The Context was stopped (e.g. switched away from) while a reconnect was queued.
            // Reviving its subscriptions here is what previously resurrected stopped contexts.
            logger.log(
                logger.Source.SERVICES,
                "Skipping WebSocket reconnect subscriptions: context is not currently subscribed.",
                this.user
            );
            return;
        }

        this.webSocketChannel.startSubscriptions();
        this.serverEvents.reconnectSubscriptions();
        // Services in this.services (scheduledDriverSafetyService, scheduledCoachingService)
        // are intentionally not reconnected here. They are polling/store-based and hold no
        // WebSocket channel topics, so there is nothing to re-register after a channel rebuild.
        // This assumption holds for the current modules; new web-app modules are not planned
        // (functionality is moving to native). If a future module does subscribe to channel
        // topics via notificationCenter.on(), it must be wired in explicitly here alongside
        // a matching reconnectSubscriptions() call. See MYG-143588.
    }
}
